"use client";

import Translations from "@translate/components/EmailLogin/steps/EmailInputStepPopup";
import React from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import type { EmailInputStepProps } from "../types";
import { LoadingButton } from "../LoadingButton";
import {
  EmailInputField,
  useEmailInputCanSubmit,
} from "../shared/EmailInputField";
import { EmailLoginOrDivider } from "../shared/EmailLoginOrDivider";
import { SocialLoginButtons } from "../shared/SocialLoginButtons";
import { EmailLoginTermsCheckbox } from "../shared/EmailLoginTermsCheckbox";

const I18n = I18nUtil.createScoped(Translations);

export function EmailInputStepPopup({
  email,
  onEmailChange,
  onContinue,
  sending = false,
  emailFormatError = false,
  agreed,
  onAgreedChange,
  termsLink,
  privacyLink,
  onSocialClick,
  socialLoading = false,
  className,
}: EmailInputStepProps) {
  const canSubmit = useEmailInputCanSubmit(email, agreed, sending);

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "flex w-full flex-col gap-6 md:items-center md:justify-center",
          className,
        )}
      >
        <div className="mx-auto flex w-full flex-col items-start items-center gap-4">
          <h2
            aria-label="Email Input Step h2"
            data-openreplay-label="login_emailinputstep_h2"
            id="sem-email-input-step-h2-2"
            className="headline_regular_s text-content_primary md:title_xl md:text-center"
          >
            {I18n.t("Sign in / Sign up")}
          </h2>
        </div>

        <div className="flex w-full flex-col gap-6 md:gap-4">
          <EmailInputField
            variant="popup"
            email={email}
            onEmailChange={onEmailChange}
            emailFormatError={emailFormatError}
            canSubmit={canSubmit}
          />
          <LoadingButton
            data-openreplay-label="login_emailloginemailinputstep_click"
            type="button"
            disabled={!canSubmit}
            loading={sending}
            onClick={onContinue}
            className={cn(
              "button_l flex h-12 w-full items-center justify-center rounded-lg transition-opacity",
              canSubmit
                ? "bg-[#036263] text-[#FEFFFA]"
                : "cursor-not-allowed bg-[#9AC0BE] text-[#D7E6E5]",
            )}
          >
            {I18n.t("Continue with email")}
          </LoadingButton>
        </div>
      </div>

      <div className="mt-4 flex flex-col">
        {onSocialClick && (
          <div className="flex flex-col gap-4">
            <EmailLoginOrDivider variant="popup" className="gap-4" />
            <SocialLoginButtons
              variant="popup"
              onSocialClick={onSocialClick}
              socialLoading={socialLoading}
            />
          </div>
        )}
        <EmailLoginTermsCheckbox
          variant="popup"
          agreed={agreed}
          onAgreedChange={onAgreedChange}
          termsLink={termsLink}
          privacyLink={privacyLink}
          className="mt-4 pb-10"
        />
      </div>
    </div>
  );
}
